"use client";

import { useEffect, useState } from "react";
import { useParams } from "next/navigation";
import { useSnapshot } from "valtio";
import log from "logbeacon";
import { ChatModalContent } from "../ChatModal/ChatModalContent";
import type { ChatOnboardingViewProps } from "./types";
import {
  resolveOnboardingGuideChatInit,
  type OnboardingGuideChatInit,
} from "@/src/hooks/onboarding/onboardingChatGuideInit";
import { useChatOnboardingApi } from "@/src/hooks/onboarding/useChatOnboardingApi";
import { fetchQuizResultForOnboarding } from "@/src/hooks/onboarding/fetchQuizResult";
import { ROUTER_PATH } from "@/src/config";
import { useShellRouter } from "@/src/hooks";
import { rootStore } from "@/src/store";
import { ShowOnPcOrMobile } from "../ShowOnPcOrMobile";
import { OnboardingWelcomeHeroPanel } from "@/src/components/OnboardingWelcomeHeroPanel";
import { AssetImage } from "@ospweb/sdk-ui";
import { ChatSkeleton } from "@components/Chat/ChatSkeleton";
import { gaTrackingAttrs } from "@/src/utils/tracking";
import { setEdgenOnboardingStepCookie } from "@/src/utils/onboarding";

export const ChatOnboardingView = ({
  className: _className,
}: ChatOnboardingViewProps) => {
  const params = useParams<{ locale?: string }>();
  const { isLoading: loginLoading, isLoggedIn } = useSnapshot(rootStore.login);
  const router = useShellRouter();
  const onboardingApi = useChatOnboardingApi();
  const [contentReady, setContentReady] = useState(false);
  const [onboardingAllowed, setOnboardingAllowed] = useState<boolean | null>(
    null,
  );

  const [guideChatInit, setGuideChatInit] =
    useState<OnboardingGuideChatInit | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const quizContextSuffix = await fetchQuizResultForOnboarding(
        params?.locale,
      );
      if (cancelled) {
        return;
      }
      const guideInit = resolveOnboardingGuideChatInit();
      const mergedGuideInit =
        guideInit.chatInfo && quizContextSuffix
          ? {
              ...guideInit,
              chatInfo: {
                ...guideInit.chatInfo,
                question: `${guideInit.chatInfo.question}${quizContextSuffix}`,
              },
            }
          : guideInit;
      log.debug(
        `[resultId-flow] onboarding first stream injected=${!!quizContextSuffix}`,
      );
      setGuideChatInit(mergedGuideInit);
    })();
    return () => {
      cancelled = true;
    };
  }, [params?.locale]);

  useEffect(() => {
    if (loginLoading || !isLoggedIn) {
      return;
    }

    let cancelled = false;

    void (async () => {
      const { shouldEnter, onboardingStage } =
        await onboardingApi.getOnboardingStatus();
      if (cancelled) {
        return;
      }

      if (!shouldEnter) {
        setEdgenOnboardingStepCookie(3);
        log.info(
          "[ChatOnboardingView] redirect to brief — onboarding not required",
          { onboardingStage },
        );
        router.replace(ROUTER_PATH.BRIEF);
        setOnboardingAllowed(false);
        return;
      }

      setOnboardingAllowed(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, loginLoading, onboardingApi, router]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setContentReady(!!isLoggedIn && !loginLoading);
    }, 2000);

    return () => clearTimeout(timer);
  }, [loginLoading, isLoggedIn]);

  if (!contentReady || guideChatInit == null || onboardingAllowed !== true) {
    return (
      <div className="px-4 w-full mt-[60px]">
        <ChatSkeleton />
      </div>
    );
  }

  return (
    <div
      className="w-full flex"
      {...gaTrackingAttrs("ob_funnel_welcome_view", {
        entry_scene: "Onboarding",
      })}
    >
      <div className="md:max-w-[534px] md:w-[534px] mx-auto w-full">
        <ShowOnPcOrMobile showMode="pc">
          <div className="pt-[60px] fixed top-0 z-[11] bg-background_solid_neutral_secondary">
            <AssetImage
              width={56}
              height={28}
              alt="edgen-logo"
              className="opacity-100 transition-opacity h-7 w-[92px]"
              src="/edgen_logo_full_v2.svg"
            />
          </div>
        </ShowOnPcOrMobile>
        <ChatModalContent
          scene="onboarding"
          chatInfo={
            guideChatInit.shouldPassChatInfo
              ? guideChatInit.chatInfo
              : undefined
          }
        />
      </div>
      <ShowOnPcOrMobile showMode="pc">
        <OnboardingWelcomeHeroPanel />
      </ShowOnPcOrMobile>
    </div>
  );
};
